import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { authService } from '../apis/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      try {
        const profileRaw = localStorage.getItem("profile");
        if (profileRaw) {
          const profile = JSON.parse(profileRaw);
          if (profile?.name && !user.name) {
            user.name = profile.name;
          } else if (profile?.name) {
            user.name = profile.name;
          }
        }
      } catch (_) { /* ignore */ }
    }
    setCurrentUser(user);
    setLoading(false);
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    await authService.logout();
    setCurrentUser(null);
    localStorage.clear();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    // Access JWT is valid for 30 days. Refresh is best-effort and must not
    // kick the user back to login (a failed refresh used to do that after login).
    const refresh = async () => {
      await authService.refreshToken();
    };

    const refreshTokenInterval = setInterval(refresh, 10 * 60 * 1000);

    return () => {
      clearInterval(refreshTokenInterval);
    };
  }, [currentUser]);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.login(email, password);
      if (result.success) {
        localStorage.setItem(
          "username",
          result?.data?.name || result?.data?.profile?.name || (email?.split?.("@")[0] ?? "")
        );        
        setCurrentUser({
          email: result.data.email,
          name: result.data.name || result.data.profile?.name || email.split('@')[0],
          isAdmin: result.data.isAdmin || false,
          role: result.data.role,
          roleid: result.data.roleid,
          userid: result.data.userid,
          permissions: result.data.permissions || {},
        });
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.signup(name, email, password);
      if (result.success) {
        setCurrentUser({
          email: result.data.email,
          name: result.data.name
        });
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      setError('An unexpected error occurred');
      return { success: false, message: 'An unexpected error occurred' };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    signup,
    logout,
    isAuthenticated: !!currentUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};